import * as React from 'react';
import Row from '@sdk/components/ui/spacing/Row';
import Text18 from '@sdk/components/ui/typography/Text18';
import ProgressBarCircular from '@sdk/components/ui/ProgressBarCircular';
import DisplayText from '@sdk/components/ui/DisplayText';
import Chevron, { ChevronDirection } from '@sdk/components/ui/Chevron';
import Text14 from '@sdk/components/ui/typography/Text14';
import Clickable from '@sdk/components/primitives/Clickable';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import type { OnboardingTrialFlag } from '@sdk/authentication/types/apiTypes';

import type { OnboardingStepsConfigType } from '../onboardingChecklist.types';
import ChevronIcon from '../../../assets/svg/UI/chevron-thin.svg';
import { getFirstUncompletedItem } from '../helpers/getFirstUncompletedItem';
import { getCompletionPercentage } from '../helpers/getCompletionPercentage';

import { ProgressBarContainer, ChevronContainer, CenterdText } from './checklistHeader.styles';

type Props = {
  isOpen: boolean;
  userName: string;
  steps: OnboardingTrialFlag[];
  config: OnboardingStepsConfigType;
  onClick: () => void;
};

const ChecklistHeader: React.FC<React.PropsWithChildren<Props>> = ({
  isOpen,
  userName,
  steps,
  config,
  onClick,
}) => {
  const allSteps = steps.length;
  const stepsCompleted = steps.filter((step) => step.completed).length;
  const firstUncompletedItem = getFirstUncompletedItem(steps);
  const completionPercentage = getCompletionPercentage(steps);

  const renderCollapsedItem = (): JSX.Element => {
    return (
      <Clickable onClick={onClick}>
        <Row alignItems="center" justifyContent="space-between">
          <Row alignItems="center">
            <ProgressBarContainer>
              <ProgressBarCircular
                progress={completionPercentage}
                size={25}
                borderWidth={2}
                themeColor={ThemeColorType.success}
              />
              <CenterdText>
                <DisplayText bold fontSize={10}>{`${stepsCompleted}/${steps.length}`}</DisplayText>
              </CenterdText>
            </ProgressBarContainer>
            <Text14 pLeft={7}>{`${config[firstUncompletedItem?.key]?.content}`}</Text14>
          </Row>
          <Row alignItems="center">
            <ChevronContainer>
              <Chevron
                chevronDirection={isOpen ? ChevronDirection.down : ChevronDirection.up}
                icon={<ChevronIcon />}
              />
            </ChevronContainer>
          </Row>
        </Row>
      </Clickable>
    );
  };

  return isOpen ? (
    <Clickable onClick={onClick}>
      <Row alignItems="center" justifyContent="space-between">
        <Text18 bold>{`Hello, ${userName}!`}</Text18>
        <Row alignItems="center">
          <ProgressBarContainer>
            <ProgressBarCircular
              progress={completionPercentage}
              size={25}
              borderWidth={2}
              themeColor={ThemeColorType.success}
            />
            <CenterdText>
              <DisplayText
                themeColor={ThemeColorType.gray}
                bold
                fontSize={10}>{`${stepsCompleted}/${allSteps}`}</DisplayText>
            </CenterdText>
          </ProgressBarContainer>
          <ChevronContainer>
            <Chevron
              chevronDirection={isOpen ? ChevronDirection.down : ChevronDirection.up}
              icon={<ChevronIcon />}
            />
          </ChevronContainer>
        </Row>
      </Row>
    </Clickable>
  ) : (
    renderCollapsedItem()
  );
};

export default ChecklistHeader;
